import React from 'react';
import { ArrowLeft, Download, FileText, AlertTriangle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/ul5sroyy_HAPPY%20MERCHANT.png';

const categories = [
  {
    title: 'Amendments / Change of Details',
    description: "Life happens and businesses evolve. Whether you're changing directors, updating bank details or modifying business information, these forms ensure your account reflects your current circumstances. Keeping your details current helps us serve you better and ensures smooth processing of your transactions.",
    forms: [
      { name: 'Additional Outlet Form (EU)', file: '/assets/additional-outlets-form-eu.pdf' },
      { name: 'Additional Outlet Form (UK)', file: '/assets/additional-outlet-form-uk.pdf' },
      { name: 'Change of Director / Shareholder', file: '/assets/change-of-director-shareholder-2026.pdf' },
      { name: 'Direct Debit Mandate Form', file: '/assets/direct-debit-mandate-form.pdf' },
      { name: 'Notification of Change of Address', file: '/assets/notification-of-change-of-address.pdf' },
      { name: 'Request to Amend Bank Account Details (EU)', file: '/assets/request-to-amend-bank-account-details-eu.pdf' },
      { name: 'Request to Amend Bank Account Details (UK)', file: '/assets/request-to-amend-bank-account-details-uk.pdf' },
    ],
  },
  {
    title: 'Information Documents',
    description: 'Access key information about your services, terms and account features. These documents provide valuable insights into your merchant account functionality and help you make the most of your payment processing capabilities.',
    forms: [
      { name: 'Benefits of DCC', file: '/assets/benefits-of-dcc.pdf' },
      { name: 'Chargeback Dispute Form', file: '/assets/chargeback-dispute-form.pdf' },
      { name: 'Chargeback Manual', file: '/assets/chargeback-manual.pdf' },
      { name: 'PCI DSS Chart', file: '/assets/pci-dss-chart.pdf' },
      { name: 'PCI DSS Quick Guide', file: '/assets/pci-dss-quick-guide.pdf' },
    ],
  },
  {
    title: 'Data Protection',
    description: 'Stay compliant and informed about how we protect your data. Our data protection documents outline our commitment to safeguarding your business and customer information in accordance with GDPR and industry best practices.',
    forms: [
      { name: 'Data Subject Request Form', file: '/assets/data-subject-request-form.pdf' },
    ],
  },
];

const FormRow = ({ form }) => (
  <a
    href={form.file}
    download
    className="flex items-center justify-between p-4 rounded-lg border border-gray-300 bg-gray-100 hover:border-[#1F4E79] hover:bg-[#1F4E79]/5 transition-all duration-200 group"
    data-testid={`form-download-${form.name.replace(/\s+/g, '-').toLowerCase()}`}
  >
    <span className="text-gray-800 font-medium group-hover:text-[#1F4E79] transition-colors duration-200">
      {form.name}
    </span>
    <div className="flex items-center gap-2 text-[#1F4E79] opacity-60 group-hover:opacity-100 transition-opacity duration-200">
      <span className="text-xs font-medium uppercase">PDF</span>
      <Download size={18} />
    </div>
  </a>
);

const BusinessForms = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col" data-testid="business-forms-page">
      <Header />

      <div className="h-20" />

      {/* Full-page background image */}
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="fixed inset-0 top-20 bg-white/0" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="forms-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          {/* Intro */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" data-testid="forms-page-title">
              Business Forms & Documents
            </h1>

            <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                Welcome to your Business Forms & Documents centre — a comprehensive collection of forms and resources designed to help you manage your merchant account efficiently. Whether you need to update your business details, access important information or handle specific account requirements, you'll find everything you need right here.
              </p>
              <p>
                Simply browse the categories below, download the relevant forms, complete them and return them to us using the submission instructions provided with each document. All forms are available in PDF format and can be completed digitally or printed for manual completion.
              </p>
            </div>
          </div>

          {/* Document Categories Header */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="forms-categories-title">
              Document Library
            </h2>
          </div>

          {/* Category Sections */}
          {categories.map((category, catIndex) => (
            <div
              key={catIndex}
              className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6"
              data-testid={`forms-category-${catIndex}`}
            >
              <h3 className="text-xl sm:text-2xl font-bold text-[#1F4E79] mb-3">
                {category.title}
              </h3>
              <p className="text-gray-700 text-base leading-relaxed mb-6">
                {category.description}
              </p>

              {category.forms.length > 0 ? (
                <div className="space-y-3">
                  {category.forms.map((form, formIndex) => (
                    <FormRow key={formIndex} form={form} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic text-sm">Documents coming soon.</p>
              )}
            </div>
          ))}

          {/* Important Notice */}
          <div className="bg-amber-50/90 backdrop-blur-sm rounded-lg shadow-lg p-6 sm:p-8 mb-6 border border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle size={22} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-800 text-base leading-relaxed">
                <strong>Important:</strong> Please ensure all forms are completed accurately and returned promptly to avoid any delays in processing your requests.
              </p>
            </div>
          </div>

          {/* Need Assistance */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="forms-need-assistance">
              Need Assistance?
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8">
              If you're unsure which form you need or require help completing any documentation, our support team is here to assist you. Contact us via phone or email for personalised guidance.
            </p>

            <div className="space-y-4">
              <div>
                <p className="font-semibold text-gray-900">Phone Support</p>
                <p className="text-gray-700">EU: +353 (0)46 901 1288</p>
                <p className="text-gray-700">UK: +44 (0)20 7183 5253</p>
              </div>

              <div>
                <p className="font-semibold text-gray-900">Email</p>
                <a
                  href="mailto:csr@ecomm365.com"
                  className="text-[#1F4E79] hover:text-[#163a5f] underline transition-colors duration-200"
                  data-testid="forms-email-link"
                >
                  csr@ecomm365.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <BackToTop />
    </div>
  );
};

export default BusinessForms;
